import { useState } from "react";
import { Tabs } from "@cntrl/ui";
import { useOrgCapability } from "../../app/orgContext.js";
import { OrgSettingsAccountTab } from "./OrgSettingsAccountTab.js";
import { OrgSettingsOrganizationTab } from "./OrgSettingsOrganizationTab.js";
import { OrgSettingsTutorialsTab } from "../settings/tutorials/SettingsTutorialsTab.js";
import { useOrgTutorials } from "../../app/hooks/useTutorials.js";
import { visibleOrgSettingsTabs, type OrgSettingsTab } from "./orgSettingsTabs.js";
import styles from "./OrgSettingsScreen.module.scss";

// Note the omission: this page never carries an ARTIST, so it never offers the Artist plan tab. That is a
// property of the chrome, not a gap — `visibleOrgSettingsTabs` is called with no artist verdict, so the key
// can never be the active one and there is nothing to map. Typed as a partial record for exactly that reason,
// rather than mapping a body that cannot be reached.
const BODIES: Partial<Record<OrgSettingsTab, () => React.ReactElement>> = {
  organization: OrgSettingsOrganizationTab,
  account: OrgSettingsAccountTab,
  tutorials: OrgSettingsTutorialsTab,
};

/**
 * The org settings PAGE (`/roster/settings`) — the same tabs the account chip's modal opens, under a page
 * heading. It survives the move to a modal for the same reason `/settings/*` did on the band side: a
 * bookmark, and a designed fallback if the dialog cannot mount. Both chromes read `orgSettingsTabs`, so
 * neither can drift on which tabs exist or who may see one.
 *
 * Tab state is LOCAL rather than routed. `/settings/connections` is routed because the OAuth connect flow
 * RETURNS to it as a cold page load; nothing returns to an org settings tab, so a `$tab` segment would be
 * URL surface with no caller.
 */
export function OrgSettingsScreen() {
  const hasTutorials = (useOrgTutorials().data?.length ?? 0) > 0;
  const tabs = visibleOrgSettingsTabs(useOrgCapability("roster:manage_seats"), false, hasTutorials);
  const [active, setActive] = useState<OrgSettingsTab>();

  // Open on the first tab this seat can SEE — Organization is `roster:manage_seats`, so for a staffer that
  // is Account. A hard-coded default would render a body with no tab selected.
  const activeTab = active && tabs.some((t) => t.key === active) ? active : tabs[0]?.key;
  if (!activeTab) return null;
  const Body = BODIES[activeTab];
  if (!Body) return null;

  return (
    <section className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>Settings</h1>
      </header>

      <Tabs tabs={tabs} active={activeTab} onChange={setActive} label="Settings sections" />

      <div className={styles.body}>
        <Body />
      </div>
    </section>
  );
}
